const MAX_GAP_DAYS = 100;

/**
 * Determines whether a course review cycle has been used (has any reviews).
 * A cycle is locked when it has any reviews (in-progress or published),
 * meaning faculty have started working within it.
 *
 * @param {Object} cycle - The review cycle object
 * @returns {boolean} true if the cycle has been used and core fields should be locked
 */
export function isCycleActive(cycle) {
  return !!cycle?.has_reviews;
}

/**
 * Determines whether a specific course can be removed from a cycle.
 * A course cannot be removed if it has associated review records.
 *
 * @param {Object} cycle - The review cycle object (must have contexts_with_reviews)
 * @param {string} courseId - The course ID to check
 * @returns {boolean} true if the course has reviews and cannot be removed
 */
export function isCourseLockedInCycle(cycle, courseId) {
  return (cycle?.contexts_with_reviews || []).includes(courseId);
}

/**
 * Validates that sequential academic years exist for a course review cycle.
 * The backend fetches all years from the cycle start_date onward, then uses
 * chunk_array to split them into groups of `duration`. We need `duration`
 * consecutive years starting from the selected start year.
 *
 * "Sequential" means each year's start_at is within MAX_GAP_DAYS of the
 * previous year's end_at. This handles both calendar-year setups (2026, 2027)
 * and academic-year setups spanning two calendar years (Fall 2025 - Summer 2026).
 *
 * @param {Object} startYear - The selected start academic year (must have id, start_at)
 * @param {number} duration - Cycle length in years
 * @param {Array} academicYears - All available academic years
 * @returns {{ valid: boolean, missingGaps: Array<{after: string, before: string}> }}
 */
export function validateSequentialYears(startYear, duration, academicYears) {
  if (!startYear?.id || !duration || !academicYears?.length) {
    return { valid: true, missingGaps: [] };
  }

  const numDuration = Number(duration);
  if (!numDuration || numDuration < 1) {
    return { valid: true, missingGaps: [] };
  }

  const sortedAcademicYears = [...academicYears].sort(
    (a, b) => new Date(a.start_at) - new Date(b.start_at)
  );

  const startIndex = sortedAcademicYears.findIndex(
    (y) => y.id === startYear.id
  );
  if (startIndex === -1) {
    return { valid: true, missingGaps: [] };
  }

  // Walk forward from the start year, counting contiguous years.
  // Stop at the first gap or when we've found enough years.
  const missingGaps = [];
  let contiguousCount = 1; // the start year itself

  for (let i = startIndex; i < sortedAcademicYears.length - 1; i++) {
    if (contiguousCount >= numDuration) {
      break;
    }

    const currentEnd = new Date(sortedAcademicYears[i].end_at);
    const nextStart = new Date(sortedAcademicYears[i + 1].start_at);
    const gapDays = (nextStart - currentEnd) / (1000 * 60 * 60 * 24);

    if (gapDays > MAX_GAP_DAYS) {
      missingGaps.push({
        after: sortedAcademicYears[i].name,
        before: sortedAcademicYears[i + 1].name,
        count: null,
      });
      break;
    }

    contiguousCount++;
  }

  if (contiguousCount < numDuration && missingGaps.length === 0) {
    // Ran out of years without hitting a gap
    const latest = sortedAcademicYears[sortedAcademicYears.length - 1];
    const deficit = numDuration - contiguousCount;
    missingGaps.push({ after: latest.name, before: null, count: deficit });
  }

  return { valid: missingGaps.length === 0, missingGaps };
}
